import { useEffect, useMemo, useRef, useState } from "react";

import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import Link from "next/link";
import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";

import { getApiErrorMessage } from "@/api";
import { ChatUnreadMessagesCount } from "@/components/chat/ChatUnreadMessagesCount";
import { AlertDialog } from "@/components/dialog/alert/AlertDialog";
import { ConfirmDialog } from "@/components/dialog/confirm/ConfirmDialog";
import { Icon } from "@/components/icons/Icon";
import { MousePositionContextMenu } from "@/components/sidebar/context-menu/MousePositionContextMenu";
import { useTooltip } from "@/components/tooltip/Tooltip";
import { useStore } from "@/store";
import { useTeamChats } from "@/store/useTeamChats";
import { chatPageUrl } from "@/urls";
import { twMerge } from "@/utils/twMerge";

import { ContextMenu } from "../documents/ContextMenu";
import { EmptyFolderText } from "../documents/EmptyFolderText";
import { ShowMoreButton } from "../documents/ListItemButton";
import { ListItemTitle } from "../documents/ListItemTitle";
import { SectionDivider } from "../SectionDivider";
import {
  SECTION_HEADER_ACTION_CLASS_NAME,
  SECTION_HEADER_ACTION_ICON_CLASS_NAME,
  SidebarSectionHeader,
} from "../SidebarSectionHeader";
import { UnreadDot } from "../UnreadDot";
import { useRowFocusWithin } from "../useRowFocusWithin";
import { NavSection, TreeItem, useTreeItemControlTabIndex } from "../useSidebarNav";

import { ChatIconPicker } from "./ChatIconPicker";
import { useCreateChat } from "./useCreateChat";

import type { DropdownItem } from "@/components/sidebar/Dropdown";
import type { ChatModel } from "@/models/chat/ChatModel";
import type { ComponentProps } from "react";

const TeamChatItem = observer(function TeamChatItem({ chat }: { chat: ChatModel }) {
  const t = useTranslations();
  const { view } = useStore();
  const teamChats = useTeamChats();
  const { chatId } = useParams<{ [key: string]: string | undefined }>();
  const isActive = chatId === chat.uid;
  const isEditing = teamChats.editChatMap.get(chat);
  const hasUnreadMessages = chat.unreadMessagesCount > 0;
  const rowRef = useRef<HTMLDivElement>(null);
  const canDelete = chat.isCollective;
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const { focusWithin, focusWithinProps } = useRowFocusWithin();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteAlertIsOpen, setDeleteAlertIsOpen] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await teamChats.deleteChat(chat);
    } catch (err) {
      setDeleteAlertIsOpen(true);
      setDeleteError(getApiErrorMessage(err));
      setIsDeleting(false);
      setIsDeleteConfirmOpen(false);
    }
  };

  const menuItems = useMemo(
    () =>
      [
        {
          title: t("documentsSidebar.renameChat"),
          icon: <Icon name="Rename" />,
          onClick: () => {
            teamChats.editChatMap.set(chat);
          },
        },
        {
          title: t("documentsSidebar.markChatAsRead"),
          icon: <Icon name="chat/MarkAsRead" />,
          disabled: !hasUnreadMessages,
          onClick: () => {
            chat.markAsRead().catch((err) => {
              console.error("Failed to mark chat as read:", err);
            });
          },
        },
        ...(canDelete
          ? [
              {
                title: t("documentsSidebar.deleteChat"),
                icon: <Icon name="Trash" />,
                onClick: () => setIsDeleteConfirmOpen(true),
              },
            ]
          : []),
      ] satisfies DropdownItem[],
    [t, chat, teamChats, hasUnreadMessages, canDelete],
  );

  return (
    <li role="none">
      <MousePositionContextMenu
        items={isEditing ? [] : menuItems}
        onOpenChange={setContextMenuOpen}
      >
        <TreeItem
          ref={rowRef}
          focused={focusWithin}
          {...focusWithinProps}
          aria-label={chat.title || t("chat.untitledChat")}
          onActivate={() => rowRef.current?.querySelector<HTMLElement>("a")?.click()}
          className={twMerge(
            "relative flex group rounded-6",
            "hover:bg-sidebar-button-hover-inverted-bg",
            "[&:has(button[data-open])]:bg-sidebar-button-hover-inverted-bg",
            isActive && "border-blue-100 dark:border-blue-900 bg-blue-100 dark:bg-blue-900",
            isEditing && "bg-sidebar-button-hover-inverted-bg ring-1 ring-blue-500",
            contextMenuOpen && "bg-sidebar-button-hover-inverted-bg",
          )}
        >
          <div className="flex items-center pl-1 shrink-0">
            <ChatIconPicker chat={chat} />
          </div>
          <Link
            tabIndex={-1}
            href={chatPageUrl({ chatId: chat.uid })}
            aria-current={isActive ? "page" : undefined}
            className={twMerge(
              "flex items-center gap-1.5 w-full min-h-[34px] pl-1.5 pr-2",
              isEditing
                ? "pr-2"
                : "fromSm:group-hover:pr-8 fromSm:group-has-[:focus-visible]:pr-8 sm:pr-14",
              !isEditing &&
                hasUnreadMessages &&
                "fromSm:pr-7 fromSm:group-hover:pr-14 fromSm:group-has-[:focus-visible]:pr-14 fromSm:group-has-[button[data-open]]:pr-14 sm:!pr-20",
            )}
            onClick={() => {
              // Same-URL link: no route change to close the phone panel
              if (isActive) {
                runInAction(() => {
                  view.isMobileDocsVisible = false;
                });
              }
            }}
          >
            <ListItemTitle
              type="chat"
              className={twMerge("py-0", hasUnreadMessages && "font-medium")}
              title={chat.title || ""}
              isEditing={isEditing}
              onUpdate={(newTitle) => {
                chat.updateTitle(newTitle.trim()).catch((err) => {
                  console.error("Failed to update chat title:", err);
                });
              }}
              onStopEdit={() => teamChats.editChatMap.reset()}
            />
          </Link>
          {!isEditing && (
            <>
              <ChatUnreadMessagesCount
                count={chat.unreadMessagesCount}
                className={twMerge(
                  "absolute top-1/2 -translate-y-1/2 right-2",
                  "group-hover:right-8 group-has-[:focus-visible]:right-8 group-has-[button[data-open]]:right-8 sm:!right-12",
                )}
              />
              <ContextMenu
                items={menuItems}
                title={chat.title || t("chat.untitledChat")}
                rowRef={rowRef}
              />
            </>
          )}
        </TreeItem>
      </MousePositionContextMenu>
      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        isLoading={isDeleting}
        title={t("documentsSidebar.deleteChatConfirmation", {
          title: chat.title || t("chat.untitledChat"),
        })}
        description={t("documentsSidebar.deleteChatDescription")}
        onConfirm={handleDelete}
        onClose={() => setIsDeleteConfirmOpen(false)}
      />
      <AlertDialog
        isOpen={deleteAlertIsOpen}
        title={t("documentsSidebar.deleteChatError")}
        description={deleteError}
        onClose={() => {
          setDeleteAlertIsOpen(false);
          setDeleteError("");
        }}
      />
    </li>
  );
});

/** A control of the section's treeitem, so its Tab stop follows the section's focus */
function SectionNewChatButton(props: ComponentProps<"button">) {
  const controlTabIndex = useTreeItemControlTabIndex();

  return (
    <button
      type="button"
      tabIndex={controlTabIndex}
      className={twMerge(SECTION_HEADER_ACTION_CLASS_NAME, "my-auto mr-[3px] sm:mr-0")}
      {...props}
    >
      <Icon
        name="chat/NewChat"
        className={twMerge("w-5 h-5", SECTION_HEADER_ACTION_ICON_CLASS_NAME)}
      />
    </button>
  );
}

export const TeamChats = observer(function TeamChats({
  visibleCount,
  rowsContainerRef,
}: {
  visibleCount?: number;
  rowsContainerRef?: React.Ref<HTMLUListElement>;
}) {
  const t = useTranslations();
  const teamChats = useTeamChats();
  const { createChat, isCreating } = useCreateChat();
  const { handlerProps, tooltip } = useTooltip({ title: t("documentsSidebar.newChat") });
  const { team, isAvailable } = teamChats;
  const isOpen = teamChats.isSectionOpen;

  useEffect(() => {
    if (!isAvailable) return;

    void teamChats.fetchChats();
  }, [team, isAvailable, teamChats]);

  if (!isAvailable) return null;

  const chats = teamChats.collectiveChats;
  const visibleChats = visibleCount === undefined ? chats : chats.slice(0, visibleCount);

  const getEmptyText = () => {
    if (teamChats.status === "error") return t("documentsSidebar.chatsError");
    if (teamChats.status === "loaded" && chats.length === 0) return t("documentsSidebar.noChats");
    return null;
  };
  const emptyText = getEmptyText();

  return (
    <NavSection
      className="mt-1.25 p-1"
      aria-label={t("documentsSidebar.channels")}
      expanded={isOpen}
      onToggle={() => teamChats.toggleSection()}
    >
      <SidebarSectionHeader
        compact
        isOpen={isOpen}
        onToggle={() => teamChats.toggleSection()}
        title={t("documentsSidebar.channels")}
      >
        {teamChats.hasUnreadChats && (
          <UnreadDot data-testid="chats-unread-dot" className="my-auto mr-2" />
        )}
        <SectionNewChatButton
          onClick={createChat}
          disabled={isCreating}
          aria-label={t("documentsSidebar.newChat")}
          {...handlerProps}
        />
        {tooltip}
      </SidebarSectionHeader>
      {isOpen && (
        <>
          <SectionDivider className="mt-1 mb-[3px]" />
          {emptyText && <EmptyFolderText>{emptyText}</EmptyFolderText>}
          {chats.length > 0 && (
            <ul ref={rowsContainerRef} role="group">
              {visibleChats.map((chat) => (
                <TeamChatItem key={chat.uid} chat={chat} />
              ))}
            </ul>
          )}
          {chats.length > visibleChats.length && (
            <ShowMoreButton
              label={t("documentsSidebar.moreChats")}
              onClick={() => teamChats.expandList()}
            />
          )}
        </>
      )}
    </NavSection>
  );
});
